import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Chip,
  CircularProgress,
  Alert,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Switch,
  Snackbar,
  Autocomplete
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { Refresh, Add, Edit } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { fetchMarketingManagers, createMarketingManager, updateMarketingManager } from '../../../services/marketing';
import { getUsers } from '../../../services/user';
import CustomPagination from '../../../components/common/CustomPagination.js';

const MarketingManagers = () => {
  const { t } = useTranslation(['common']);
  const theme = useTheme();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [managers, setManagers] = useState([]);
  const [filters, setFilters] = useState({
    search: '',
    status: '',
    is_available_for_auto_assignment: ''
  });
  const [pagination, setPagination] = useState({
    page: 0,
    rowsPerPage: 20,
    totalCount: 0
  });
  const [customRowsInput, setCustomRowsInput] = useState('');
  const [customRowsActive, setCustomRowsActive] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({
    user_id: '',
    employee_id: '',
    full_name: '',
    phone: '',
    email: '',
    department: 'Marketing',
    hire_date: '',
    status: 'active',
    max_customers: 50,
    is_available_for_auto_assignment: true,
    notes: ''
  });
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [searchTimeout, setSearchTimeout] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [saving, setSaving] = useState(false);

  const totalPages = Math.ceil(pagination.totalCount / pagination.rowsPerPage) || 1;

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {
        ...filters,
        page: pagination.page + 1,
        page_size: pagination.rowsPerPage
      };
      
      Object.keys(params).forEach(key => {
        if (params[key] === '' || params[key] === null || params[key] === undefined) {
          delete params[key];
        }
      });

      const response = await fetchMarketingManagers(params);
      
      if (response.results) {
        setManagers(response.results);
        setPagination(prev => ({
          ...prev,
          totalCount: response.count || 0
        }));
      } else {
        setManagers(Array.isArray(response) ? response : []);
      }
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Failed to fetch marketing managers');
      setManagers([]);
    } finally {
      setLoading(false);
    }
  }, [filters, pagination.page, pagination.rowsPerPage]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
    setPagination(prev => ({ ...prev, page: 0 }));
  };

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  const handleRowsPerPageChange = (newRowsPerPage) => {
    setPagination(prev => ({ ...prev, rowsPerPage: newRowsPerPage, page: 0 }));
  };

  const handleCustomRowsInputChange = (value) => {
    setCustomRowsInput(value);
  };

  const handleCustomRowsInputBlur = () => {
    const value = parseInt(customRowsInput);
    if (value && value > 0 && value <= 1000) {
      setPagination(prev => ({ ...prev, rowsPerPage: value, page: 0 }));
      setCustomRowsActive(false);
      setCustomRowsInput('');
    } else {
      setCustomRowsInput('');
      setCustomRowsActive(false);
    }
  };

  const fetchUsersForSelection = async (searchTerm = '') => {
    setUsersLoading(true);
    try {
      const response = await getUsers({
        page_size: 20,
        ...(searchTerm && { search: searchTerm })
      });
      setUsers(response.results || response || []);
    } catch (err) {
      console.error('Failed to fetch users:', err);
      setUsers([]);
    } finally {
      setUsersLoading(false);
    }
  };

  const handleOpenModal = async (manager = null) => {
    if (manager) {
      setEditId(manager.id);
      setForm({
        user_id: manager.user?.id || manager.user || '',
        employee_id: manager.employee_id || '',
        full_name: manager.full_name || '',
        phone: manager.phone || '',
        email: manager.email || manager.user?.email || '',
        department: manager.department || 'Marketing',
        hire_date: manager.hire_date || '',
        status: manager.status || 'active',
        max_customers: manager.max_customers || 50,
        is_available_for_auto_assignment: manager.is_available_for_auto_assignment !== undefined ? manager.is_available_for_auto_assignment : true,
        notes: manager.notes || ''
      });
    } else {
      setEditId(null);
      setUsers([]); // Clear users for new manager creation
      setSelectedUser(null); // Clear selected user for new manager
      setUsersLoading(false);
      if (searchTimeout) {
        clearTimeout(searchTimeout);
        setSearchTimeout(null);
      }
      setForm({
        user_id: '',
        employee_id: '',
        full_name: '',
        phone: '',
        email: '',
        department: 'Marketing',
        hire_date: '',
        status: 'active',
        max_customers: 50,
        is_available_for_auto_assignment: true,
        notes: ''
      });
    }
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setEditId(null);
    setUsers([]); // Clear users when closing modal
    setSelectedUser(null); // Clear selected user
    setUsersLoading(false);
    if (searchTimeout) {
      clearTimeout(searchTimeout);
      setSearchTimeout(null);
    }
    setForm({
      user_id: '',
      employee_id: '',
      full_name: '',
      phone: '',
      email: '',
      department: 'Marketing',
      hire_date: '',
      status: 'active',
      max_customers: 50,
      is_available_for_auto_assignment: true,
      notes: ''
    });
  };

  const handleFormChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    // Validation
    if (!form.user_id && !editId) {
      setSnackbar({ open: true, message: 'Please select a user', severity: 'error' });
      return;
    }

    if (!form.employee_id) {
      setSnackbar({ open: true, message: 'Employee ID is required', severity: 'error' });
      return;
    }

    if (!form.full_name) {
      setSnackbar({ open: true, message: 'Full name is required', severity: 'error' });
      return;
    }

    if (form.max_customers < 1) {
      setSnackbar({ open: true, message: 'Max customers must be at least 1', severity: 'error' });
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...(editId ? {} : { user: form.user_id }),
        employee_id: form.employee_id,
        full_name: form.full_name,
        phone: form.phone || '',
        email: form.email || '',
        department: form.department || 'Marketing',
        hire_date: form.hire_date || null,
        status: form.status,
        max_customers: parseInt(form.max_customers),
        is_available_for_auto_assignment: form.is_available_for_auto_assignment,
        notes: form.notes || ''
      };

      if (editId) {
        await updateMarketingManager(editId, payload);
        setSnackbar({ open: true, message: 'Marketing manager updated successfully', severity: 'success' });
      } else {
        await createMarketingManager(payload);
        setSnackbar({ open: true, message: 'Marketing manager created successfully', severity: 'success' });
      }
      
      handleCloseModal();
      fetchData();
    } catch (err) {
      setSnackbar({ 
        open: true, 
        message: err.response?.data?.detail || err.response?.data?.message || err.message || 'Failed to save marketing manager', 
        severity: 'error' 
      });
    } finally {
      setSaving(false);
    }
  };

  const rowsWithSerial = useMemo(() => {
    return managers.map((manager, index) => ({
      ...manager,
      sn: pagination.page * pagination.rowsPerPage + index + 1
    }));
  }, [managers, pagination.page, pagination.rowsPerPage]);

  const columns = useMemo(() => [
    {
      field: 'sn',
      headerName: 'S/N',
      width: 80,
      sortable: false,
      filterable: false
    },
    {
      field: 'employee_id',
      headerName: 'Employee ID',
      flex: 1,
      minWidth: 120,
      sortable: false
    },
    {
      field: 'user',
      headerName: 'User',
      flex: 1,
      minWidth: 200,
      sortable: false,
      renderCell: (params) => {
        const user = params.row.user;
        if (!user) return 'N/A';
        
        if (typeof user === 'object') {
          return (
            <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', height: '100%' }}>
              <Typography variant="body2" sx={{ fontWeight: 500, lineHeight: 1.2 }}>
                {user.username || 'N/A'}
              </Typography>
              {(user.email || user.phone) && (
                <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.2 }}>
                  {user.email || user.phone}
                </Typography>
              )}
            </Box>
          );
        }
        return user;
      }
    },
    {
      field: 'full_name',
      headerName: 'Full Name',
      flex: 1,
      minWidth: 150,
      sortable: false
    },
    {
      field: 'phone',
      headerName: 'Phone',
      flex: 1,
      minWidth: 120,
      sortable: false,
      renderCell: (params) => {
        const user = params.row.user;
        if (!user) return 'N/A';
        
        if (typeof user === 'object') {
          return (
            <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', height: '100%' }}>
              <Typography variant="body2" sx={{ fontWeight: 500, lineHeight: 1.2 }}>
                {user.phone || 'N/A'}
              </Typography>
            </Box>
          );
        }
        return user;
      }
    },
    {
      field: 'email',
      headerName: 'Email',
      flex: 1,
      minWidth: 180,
      sortable: false,
      renderCell: (params) => {
        const user = params.row.user;
        if (!user) return 'N/A';
        
        if (typeof user === 'object') {
          return (
            <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', height: '100%' }}>
              {(user.email ) && (
                <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.2 }}>
                  {user.email}
                </Typography>
              )}
            </Box>
          );
        }
        return user;
      }
    },
    {
      field: 'department',
      headerName: 'Department',
      flex: 1,
      minWidth: 120,
      sortable: false
    },
    {
      field: 'max_customers',
      headerName: 'Max Customers',
      width: 130,
      sortable: false
    },
    {
      field: 'current_customer_count',
      headerName: 'Current Customers',
      width: 150,
      sortable: false
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 120,
      sortable: false,
      renderCell: (params) => {
        const status = params.value || 'active';
        return (
          <Chip
            label={status.charAt(0).toUpperCase() + status.slice(1)}
            color={status === 'active' ? 'success' : 'default'}
            size="small"
          />
        );
      }
    },
    {
      field: 'is_available_for_auto_assignment',
      headerName: 'Auto Assignment',
      width: 140,
      sortable: false,
      renderCell: (params) => {
        return (
          <Chip
            label={params.value ? 'Yes' : 'No'}
            color={params.value ? 'primary' : 'default'}
            size="small"
          />
        );
      }
    },
    {
      field: 'created_at',
      headerName: 'Created At',
      flex: 1,
      minWidth: 150,
      sortable: false,
      renderCell: (params) => {
        if (!params.value) return 'N/A';
        try {
          return format(new Date(params.value), 'MMM dd, yyyy');
        } catch {
          return 'N/A';
        }
      }
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 100,
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        return (
          <Button
            size="small"
            startIcon={<Edit />}
            onClick={() => handleOpenModal(params.row)}
            sx={{ color: '#DC143C' }}
          >
            Edit
          </Button>
        );
      }
    }
  ], []);

  if (loading && managers.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '80vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ 
      minHeight: '100vh', 
      backgroundColor: 'background.default',
      px: { xs: 0.75, sm: 1, md: 1.5 },
      py: { xs: 2, sm: 2.5, md: 3 },
      maxWidth: '95vw',
      overflowX: 'hidden'
    }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#DC143C', mb: 2 }}>
          Marketing Managers
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={fetchData}
            disabled={loading}
          >
            {t('buttons.refresh')}
          </Button>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => handleOpenModal()}
            sx={{ backgroundColor: '#DC143C', '&:hover': { backgroundColor: '#B91C3C' } }}
          >
            Add Manager
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <TextField
                label="Search"
                fullWidth
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                placeholder="Search by name, email, phone..."
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <TextField
                select
                label="Status"
                fullWidth
                value={filters.status}
                onChange={(e) => handleFilterChange('status', e.target.value)}
                SelectProps={{ native: true }}
              >
                <option value="">All</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <TextField
                select
                label="Auto Assignment"
                fullWidth
                value={filters.is_available_for_auto_assignment}
                onChange={(e) => handleFilterChange('is_available_for_auto_assignment', e.target.value)}
                SelectProps={{ native: true }}
              >
                <option value="">All</option>
                <option value="true">Available</option>
                <option value="false">Not Available</option>
              </TextField>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Card>
        <CardContent sx={{ p: 0 }}>
          <Box sx={{ 
            width: '100%',
            overflowX: 'auto',
            minHeight: 520
          }}>
            <DataGrid
              rows={rowsWithSerial}
              columns={columns}
              disableSelectionOnClick
              loading={loading}
              hideFooterPagination
              hideFooter
              getRowSpacing={() => ({ top: 2, bottom: 2 })}
              sx={{
                border: 'none',
                minWidth: 1200,
                '& .MuiDataGrid-columnHeader': {
                  backgroundColor: theme.palette.primary.main,
                },
                '& .MuiDataGrid-columnHeaderTitle': {
                  color: 'white',
                  fontWeight: 'bold',
                  fontSize: '1rem'
                },
                '& .MuiDataGrid-columnHeader .MuiSvgIcon-root': {
                  color: 'white'
                },
                '& .MuiDataGrid-row:hover': {
                  backgroundColor: '#FFF0F3'
                },
                '& .MuiDataGrid-cell': {
                  fontSize: '1rem'
                },
                '& .MuiDataGrid-row': {
                  marginBottom: '4px',
                },
              }}
            />
          </Box>
        </CardContent>
      </Card>

      <Box sx={{ mt: 3 }}>
        <CustomPagination
          page={pagination.page}
          totalPages={totalPages}
          rowsPerPage={pagination.rowsPerPage}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
          customRowsActive={customRowsActive}
          customRowsInput={customRowsInput}
          onCustomRowsInputChange={handleCustomRowsInputChange}
          onCustomRowsInputBlur={handleCustomRowsInputBlur}
        />
      </Box>

      {/* Add/Edit Manager Modal */}
      <Dialog open={modalOpen} onClose={handleCloseModal} maxWidth="md" fullWidth>
        <DialogTitle>
          {editId ? 'Edit Marketing Manager' : 'Add Marketing Manager'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Grid container spacing={2}>
              {!editId && (
                <Grid size={{ xs: 12 }}>
                  <Autocomplete
                    options={users}
                    getOptionLabel={(option) => {
                      const displayParts = [];
                      if (option.username) displayParts.push(`@${option.username}`);
                      if (option.email) displayParts.push(option.email);
                      if (option.fullname) displayParts.push(`(${option.fullname})`);
                      if (option.phone) displayParts.push(`Phone: ${option.phone}`);
                      return displayParts.join(' ');
                    }}
                    loading={usersLoading}
                    onInputChange={(event, newInputValue, reason) => {
                      // Don't search if the user is selecting an option
                      if (reason === 'reset' || reason === 'select-option') {
                        return;
                      }

                      // Clear previous timeout
                      if (searchTimeout) {
                        clearTimeout(searchTimeout);
                      }

                      if (newInputValue && newInputValue.length >= 2) {
                        setUsersLoading(true);
                        const timeoutId = setTimeout(() => {
                          fetchUsersForSelection(newInputValue);
                        }, 300); // 300ms debounce
                        setSearchTimeout(timeoutId);
                      } else if (!newInputValue) {
                        // Only clear users if no user is selected
                        if (!form.user_id) {
                          setUsers([]);
                        }
                        setUsersLoading(false);
                      }
                    }}
                    onChange={(event, newValue) => {
                      setSelectedUser(newValue);
                      handleFormChange('user_id', newValue?.sid || newValue?.id || '');
                      if (newValue) {
                        handleFormChange('email', newValue.email || '');
                        handleFormChange('full_name', newValue.fullname || '');
                      } else {
                        // Clear selected user when selection is cleared
                        setSelectedUser(null);
                      }
                    }}
                    value={selectedUser}
                    filterOptions={(x) => x} // Disable built-in filtering since we do server-side search
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Search Users (Email, Phone, Username, Full Name)"
                        required
                        placeholder="Type at least 2 characters to search..."
                        helperText="Search by email, phone number, username, or full name"
                      />
                    )}
                  />
                </Grid>
              )}
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  label="Employee ID"
                  fullWidth
                  required
                  value={form.employee_id}
                  onChange={(e) => handleFormChange('employee_id', e.target.value)}
                  placeholder="e.g., MM001"
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  label="Full Name"
                  fullWidth
                  required
                  value={form.full_name}
                  onChange={(e) => handleFormChange('full_name', e.target.value)}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  label="Phone"
                  fullWidth
                  value={form.phone}
                  onChange={(e) => handleFormChange('phone', e.target.value)}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  label="Email"
                  fullWidth
                  type="email"
                  value={form.email}
                  onChange={(e) => handleFormChange('email', e.target.value)}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  label="Department"
                  fullWidth
                  value={form.department}
                  onChange={(e) => handleFormChange('department', e.target.value)}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  label="Hire Date"
                  fullWidth
                  type="date"
                  value={form.hire_date}
                  onChange={(e) => handleFormChange('hire_date', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  select
                  label="Status"
                  fullWidth
                  value={form.status}
                  onChange={(e) => handleFormChange('status', e.target.value)}
                  SelectProps={{ native: true }}
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                  <option value="suspended">Suspended</option>
                </TextField>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  label="Max Customers"
                  fullWidth
                  required
                  type="number"
                  value={form.max_customers}
                  onChange={(e) => handleFormChange('max_customers', e.target.value)}
                  inputProps={{ min: 1 }}
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={form.is_available_for_auto_assignment}
                      onChange={(e) => handleFormChange('is_available_for_auto_assignment', e.target.checked)}
                    />
                  }
                  label="Available for Auto Assignment"
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <TextField
                  label="Notes"
                  fullWidth
                  multiline
                  rows={3}
                  value={form.notes}
                  onChange={(e) => handleFormChange('notes', e.target.value)}
                  placeholder="Additional notes..."
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal} disabled={saving}>
            Cancel
          </Button>
          <Button 
            onClick={handleSave} 
            variant="contained"
            disabled={saving}
            sx={{ backgroundColor: '#DC143C', '&:hover': { backgroundColor: '#B91C3C' } }}
          >
            {saving ? <CircularProgress size={20} /> : (editId ? 'Update' : 'Create')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default MarketingManagers;
